import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Card,
  Divider,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  useComputedColorScheme,
} from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import {
  IconAlertTriangle,
  IconChevronRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { fetchBrandMentions } from "./mentionsApi";
import LlmProviderLogo from "../LlmProviders/LlmProviderLogo";
import QueryTypeBadge from "../_Shared/QueryTypeBadge";
import { presenceClassMeta } from "../_Shared/presenceClasses";
import BrandAliasesCard from "./BrandAliasesCard";
import PromptSamplesDrawer from "./PromptSamplesDrawer";
import { hostOf } from "../_Shared/domains";
import SourceExplorerDrawer from "./SourceExplorerDrawer";

const WINDOWS = [
  { value: "7", label: "7 days" },
  { value: "28", label: "28 days" },
  { value: "90", label: "90 days" },
];

/**
 * Below this, an aided prompt is not producing the mention it is guaranteed to produce. That is a
 * broken alias or a surface that stopped answering, and it is worth saying so — an aided rate is
 * the one place on this page where a LOW number is the finding. Above it the row stays a single
 * quiet line, because "the answer repeated the name we put in the question" is not news.
 */
const AIDED_HEALTH_FLOOR = 0.95;

/**
 * Below this share, failed fetches are a footnote rather than a banner. One failure in 147 does
 * not change any number on the page, and a full-width warning about it outranks the measurement
 * it is a footnote to. Above it the failures are eating enough of the window to explain a thin
 * sample, so they get said out loud.
 */
const FAILURE_ALERT_SHARE = 0.1;

const pct = (v) => `${Math.round(v * 100)}%`;

/**
 * A single ratio against a limit, so: a meter, not a chart.
 *
 * The confidence band is the point of the component. A detection rate without its interval
 * invites the reader to treat 57% and 61% as different when at these sample sizes they are
 * the same measurement. Track is recessive, the band is one hue, the point estimate is a
 * marker on top — no second hue, no gradient, no axis.
 *
 * Kept narrow by its caller. A meter stretched over the full page width reads as a progress bar
 * for the page rather than as the scale a small percentage sits on.
 */
function ConfidenceMeter({ rate, low, high, color = "blue" }) {
  const bandLeft = `${low * 100}%`;
  const bandWidth = `${Math.max(high - low, 0.005) * 100}%`;

  return (
    <Tooltip
      label={`Best estimate ${pct(rate)} · 95% confidence ${pct(low)}–${pct(high)}`}
      withArrow
    >
      <Box
        pos="relative"
        h={10}
        w="100%"
        style={{
          borderRadius: 4,
          background: "var(--mantine-color-default-border)",
          cursor: "default",
        }}
      >
        <Box
          pos="absolute"
          top={0}
          bottom={0}
          style={{
            left: bandLeft,
            width: bandWidth,
            borderRadius: 4,
            background: `var(--mantine-color-${color}-light)`,
          }}
        />
        <Box
          pos="absolute"
          top={-2}
          bottom={-2}
          style={{
            left: `calc(${rate * 100}% - 1px)`,
            width: 2,
            borderRadius: 1,
            background: `var(--mantine-color-${color}-filled)`,
          }}
        />
      </Box>
    </Tooltip>
  );
}

/** An info affordance that costs one line instead of a paragraph. */
function Explainer({ text }) {
  return (
    <Tooltip label={text} multiline w={340} withArrow position="top-start">
      <IconInfoCircle
        size={14}
        style={{ opacity: 0.55, flexShrink: 0, cursor: "help" }}
        aria-label={text}
      />
    </Tooltip>
  );
}

/**
 * The headline. Renders a percentage ONLY when the sample supports one.
 *
 * At n=7 the 95% interval is about ±30 points, so a percentage there is invented precision —
 * the backend says so via isReportableAsPercentage and this refuses to override it.
 */
function DetectionHeadline({ rate, label, citedNotNamed = 0 }) {
  const enoughData = rate.samples > 0;
  const showPercentage = enoughData && rate.isReportableAsPercentage;

  return (
    <Stack gap={6}>
      <Group gap={6}>
        <Text fz="sm" c="dimmed">
          {label}
        </Text>
        <Explainer
          text={
            "Counted over prompts that do not contain your brand name. Prompts that do name you " +
            "produce a mention by construction and are counted separately, on the line below — " +
            "mixing them in would make this number rise every time you add one."
          }
        />
      </Group>

      {!enoughData && (
        <>
          <Text fz={44} fw={700} lh={1.1} c="dimmed">
            —
          </Text>
          <Text fz="sm" c="dimmed">
            No usable samples in this window yet.
          </Text>
        </>
      )}

      {enoughData && showPercentage && (
        <>
          <Text fz={44} fw={700} lh={1.1}>
            {pct(rate.rate)}
          </Text>
          <Box maw={280}>
            <ConfidenceMeter rate={rate.rate} low={rate.low} high={rate.high} />
          </Box>
          <Text fz="xs" c="dimmed">
            {rate.mentions} of {rate.samples} checks · 95% confidence {pct(rate.low)}–
            {pct(rate.high)}
          </Text>
        </>
      )}

      {enoughData && !showPercentage && (
        <>
          <Text fz={44} fw={700} lh={1.1}>
            {rate.mentions}
            <Text span fz={22} c="dimmed">
              {" "}
              / {rate.samples}
            </Text>
          </Text>
          <Text fz="xs" c="dimmed">
            Too few checks to state a percentage — the margin of error would be about ±
            {Math.round(((rate.high - rate.low) / 2) * 100)} points.
          </Text>
        </>
      )}

      {/* What is left over once a citation stopped counting as a mention. It is the difference
          between "the surface has never heard of you" and "the surface read your page and named
          somebody else", and only the second one is a content problem. */}
      {citedNotNamed > 0 && (
        <Group gap={4} mt={2}>
          <Text fz="xs" c="dimmed">
            Cited as a source in {citedNotNamed} of these checks without being named
          </Text>
          <Explainer
            text={
              "The answer linked one of your pages as a citation and named other companies in " +
              "the text. A reader sees “[3]”, not your name, so it does not count as a mention " +
              "— but the surface has your material and chose somebody else to talk about."
            }
          />
        </Group>
      )}
    </Stack>
  );
}

/**
 * Where the average score stops being a label and becomes a wash. The backend's
 * LlmResponseAnalysis.SentimentLabel uses the same cut, and it is defined once there — this
 * mirrors it only to name the reading, never to recompute the counts.
 */
const SENTIMENT_FLOOR = 0.1;

const SENTIMENT_TONES = {
  positive: { label: "Positive", color: "var(--mantine-color-green-6)" },
  neutral: { label: "Neutral", color: "var(--mantine-color-gray-5)" },
  negative: { label: "Negative", color: "var(--mantine-color-red-6)" },
};

/**
 * How the brand was talked about, from the weekly analysis.
 *
 * This is the aided row's actual finding. "Was the name repeated back" is guaranteed by the
 * prompt; how it was repeated back is not, and only the weekly analysis can say — detection
 * samples are a string match and carry no reading of the answer.
 *
 * The counts lead and the average is in the tooltip. The monitor analyses one answer per prompt
 * per surface per week, so a short window holds about as many analyses as the brand has prompts,
 * and at that size a signed decimal is invented precision while "9 positive, 1 negative" is not.
 */
function SentimentReading({ sentiment }) {
  if (!sentiment) {
    return (
      <Group gap={4} wrap="nowrap">
        <Text fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
          Not analysed in this window
        </Text>
        <Explainer
          text={
            "Detection runs daily and only checks whether your name appeared. How the answer " +
            "talks about you comes from the weekly monitor run, and none has landed inside this " +
            "window yet — try a longer one."
          }
        />
      </Group>
    );
  }

  const tone =
    sentiment.average > SENTIMENT_FLOOR
      ? SENTIMENT_TONES.positive
      : sentiment.average < -SENTIMENT_FLOOR
        ? SENTIMENT_TONES.negative
        : SENTIMENT_TONES.neutral;

  const parts = [];
  if (sentiment.positive > 0) parts.push(`${sentiment.positive} positive`);
  if (sentiment.neutral > 0) parts.push(`${sentiment.neutral} neutral`);

  return (
    <Tooltip
      label={`Average score ${sentiment.average > 0 ? "+" : ""}${sentiment.average} across ${
        sentiment.analyses
      } answer${sentiment.analyses === 1 ? "" : "s"} read by the weekly monitor${
        sentiment.lastAnalysedAt
          ? `, most recently ${new Date(sentiment.lastAnalysedAt).toLocaleDateString()}`
          : ""
      }.`}
      withArrow
      multiline
      w={320}
    >
      <Group gap={6} wrap="nowrap" style={{ cursor: "help" }}>
        <Box w={8} h={8} style={{ borderRadius: 4, background: tone.color, flexShrink: 0 }} />
        <Text fz="sm" fw={600} style={{ whiteSpace: "nowrap" }}>
          {tone.label}
        </Text>
        <Text fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
          {parts.join(" · ")}
          {sentiment.negative > 0 && parts.length > 0 && " · "}
        </Text>
        {sentiment.negative > 0 && (
          <Text fz="xs" c="red.6" fw={600} style={{ whiteSpace: "nowrap" }}>
            {sentiment.negative} negative
          </Text>
        )}
      </Group>
    </Tooltip>
  );
}

/**
 * A class other than the headline one, as one line.
 *
 * For the aided class the reading on the right is the SENTIMENT, not the rate. Being named back
 * by a prompt that names you is guaranteed, so the percentage answers nothing — it is demoted to
 * a health check in dimmed type, and only speaks up when it falls below the floor, where it stops
 * meaning "we were mentioned" and starts meaning "a spelling or a surface broke".
 */
function ClassRow({ entry }) {
  const meta = presenceClassMeta(entry.class);
  const { rate, sentiment } = entry;
  const showPercentage = rate.samples > 0 && rate.isReportableAsPercentage;
  const isAided = entry.class === "aided";
  const isLow = isAided && showPercentage && rate.rate < AIDED_HEALTH_FLOOR;

  const named =
    rate.samples === 0
      ? "no checks yet"
      : showPercentage
        ? `named in ${pct(rate.rate)} of ${rate.samples} checks`
        : `named in ${rate.mentions} of ${rate.samples} checks`;

  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <Badge size="sm" color={meta.color} variant="light">
            {meta.shortLabel}
          </Badge>
          <Text fz="sm" truncate>
            {meta.label}
          </Text>
          <Explainer text={meta.meaning} />
        </Group>

        {isAided ? (
          <Group gap="sm" wrap="nowrap">
            <SentimentReading sentiment={sentiment} />
            <Text fz="xs" c={isLow ? "warning" : "dimmed"} style={{ whiteSpace: "nowrap" }}>
              {named}
            </Text>
            {isLow && (
              <Badge
                size="sm"
                color="warning"
                variant="light"
                leftSection={<IconAlertTriangle size={12} />}
              >
                below expected
              </Badge>
            )}
          </Group>
        ) : (
          <Group gap="sm" wrap="nowrap">
            <Text fz="sm" fw={600}>
              {rate.samples === 0 ? (
                "—"
              ) : showPercentage ? (
                pct(rate.rate)
              ) : (
                <>
                  {rate.mentions}
                  <Text span c="dimmed" fw={400}>
                    {" "}
                    / {rate.samples}
                  </Text>
                </>
              )}
            </Text>
            <Text fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              n = {rate.samples} · {entry.prompts} prompt{entry.prompts === 1 ? "" : "s"}
            </Text>
          </Group>
        )}
      </Group>

      {isLow && (
        <Text fz="xs" c="warning">
          These prompts contain your brand name, so the answer should almost always repeat it.
          A reading this low usually means a spelling we do not match yet — check the spellings
          at the bottom of this page.
        </Text>
      )}
    </Stack>
  );
}

/**
 * One tile per surface, rendered only when there is more than one.
 *
 * With a single surface the tile restates the headline in smaller type against the same n, which
 * is where the reader starts hunting for the difference between two numbers that have none. The
 * surface name belongs on the summary card in that case, not in a box of its own.
 */
function SurfaceTile({ surface }) {
  const rate = surface.unaidedRate;

  return (
    <Card withBorder padding="md">
      <Group gap="xs" mb="xs">
        <LlmProviderLogo llmProvider={surface.surface} size={18} />
        <Text fw={600} fz="sm">
          {surface.surfaceName}
        </Text>
      </Group>

      {rate.samples === 0 ? (
        <Stack gap={4}>
          <Text fz="sm" c="dimmed">
            No unaided samples
          </Text>
          {surface.totalSamples > 0 && (
            <Text fz="xs" c="dimmed">
              {surface.totalSamples} check{surface.totalSamples === 1 ? "" : "s"} ran here, but
              every prompt behind them names your brand.
            </Text>
          )}
        </Stack>
      ) : (
        <Stack gap={6}>
          <Group justify="space-between" align="baseline">
            <Text fz={24} fw={700} lh={1.1}>
              {rate.isReportableAsPercentage ? (
                pct(rate.rate)
              ) : (
                <>
                  {rate.mentions}
                  <Text span fz="md" c="dimmed">
                    {" "}
                    / {rate.samples}
                  </Text>
                </>
              )}
            </Text>
            <Text fz="xs" c="dimmed">
              n = {rate.samples} unaided
            </Text>
          </Group>

          {rate.isReportableAsPercentage && (
            <ConfidenceMeter rate={rate.rate} low={rate.low} high={rate.high} />
          )}

          {surface.totalFailures > 0 && (
            <Text fz="xs" c="dimmed">
              {surface.totalFailures} failed check
              {surface.totalFailures === 1 ? "" : "s"} excluded
            </Text>
          )}
        </Stack>
      )}
    </Card>
  );
}

/**
 * Colours for the share-of-voice ring, one selected set per scheme.
 *
 * Both columns come from a palette validated for lightness band, chroma, CVD separation and
 * contrast against the chart surface — the dark column is the same hues re-stepped for a dark
 * background, not an automatic flip of the light one. The gray tail slot is deliberately below
 * the chroma floor: its job is to read as "everyone else", not as an identity.
 */
const SHARE_PALETTE = {
  light: {
    brand: "#2a78d6",
    competitors: ["#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#4a3aa7"],
    tail: "#9ca3af",
  },
  dark: {
    brand: "#3987e5",
    competitors: ["#d95926", "#199e70", "#c98500", "#d55181", "#9085e9"],
    tail: "#6b7280",
  },
};

/** Hues are never cycled: past this many competitors the tail folds into one gray slice. */
const MAX_COLOURED_COMPETITORS = SHARE_PALETTE.dark.competitors.length;

/**
 * Who the model names when the buyer has not named anyone.
 *
 * The counts are commensurate by construction — each is "unaided answers in which this brand was
 * named", over the same sample set — which is what makes them comparable at all. They are NOT a
 * partition of the answers: one answer routinely names four brands. So the ring is a share of
 * names given, never of answers, and the caption says so, because a ring silently implies that
 * its slices add up to the thing it sits next to.
 *
 * The ranked list beside it is the chart that answers the precise question; the ring is there for
 * the gestalt. Every slice is direct-labelled by the list, so identity never rests on colour.
 */
function ShareOfVoiceCard({ brandName, unaided, competitors }) {
  const scheme = useComputedColorScheme("dark");
  const palette = SHARE_PALETTE[scheme] ?? SHARE_PALETTE.dark;

  const { rows, donutData, total, myShare, hiddenCount } = useMemo(() => {
    const mine = {
      key: "__brand__",
      name: brandName || "You",
      count: unaided.mentions,
      isBrand: true,
    };
    const rivals = (competitors ?? []).map((c) => ({
      key: c.brandName,
      name: c.brandName,
      count: c.mentionCount,
      isBrand: false,
    }));

    const totalNames = mine.count + rivals.reduce((s, r) => s + r.count, 0);

    // Colour is assigned over the alphabetical order of the slices, not over the ranking, so a
    // competitor keeps its colour when the ranking moves under it. Only the slices that survive
    // the fold get a hue — the tail is one gray, never a generated sixth colour.
    const coloured = [...rivals]
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_COLOURED_COMPETITORS)
      .filter((r) => r.count > 0);

    const colourByName = new Map(
      [...coloured]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((r, i) => [r.key, palette.competitors[i]]),
    );

    const folded = rivals.filter((r) => r.count > 0 && !colourByName.has(r.key));
    const foldedCount = folded.reduce((s, r) => s + r.count, 0);

    const slices = [
      ...(mine.count > 0
        ? [{ name: mine.name, value: mine.count, color: palette.brand }]
        : []),
      ...coloured.map((r) => ({
        name: r.name,
        value: r.count,
        color: colourByName.get(r.key),
      })),
      ...(foldedCount > 0
        ? [
            {
              name: `${folded.length} other brand${folded.length === 1 ? "" : "s"}`,
              value: foldedCount,
              color: palette.tail,
            },
          ]
        : []),
    ].sort((a, b) => b.value - a.value);

    return {
      rows: [mine, ...rivals]
        .sort((a, b) => b.count - a.count)
        .map((r) => ({
          ...r,
          colour: r.isBrand ? palette.brand : (colourByName.get(r.key) ?? palette.tail),
        })),
      donutData: slices,
      total: totalNames,
      myShare: totalNames > 0 ? mine.count / totalNames : 0,
      hiddenCount: folded.length,
    };
  }, [brandName, unaided.mentions, competitors, palette]);

  const maxCount = rows[0]?.count ?? 0;

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={2}>
        <Group gap={6}>
          <Text fw={600}>Who gets named</Text>
          <Explainer
            text={
              "Counted over prompts that do not name you — the answers where the model chose who " +
              "to bring up. Answers to prompts that name you are excluded: those cite your own " +
              "site and would describe your prompt list rather than the market."
            }
          />
        </Group>
        {total > 0 && (
          <Text fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {total} name{total === 1 ? "" : "s"} given
          </Text>
        )}
      </Group>

      {total === 0 ? (
        <Text fz="sm" c="dimmed" mt="xs">
          No brand at all was named in the {unaided.samples} answer
          {unaided.samples === 1 ? "" : "s"} to prompts that do not name you.
        </Text>
      ) : (
        <>
          <Text fz="xs" c="dimmed" mb="md">
            {unaided.mentions === 0
              ? `You were not named once. Your competitors were named ${total} time${
                  total === 1 ? "" : "s"
                }.`
              : `You hold ${pct(myShare)} of the names given — ${unaided.mentions} of ${total}.`}
          </Text>

          <Group align="flex-start" gap="xl" wrap="nowrap">
            {donutData.length > 0 && (
              <DonutChart
                data={donutData}
                size={168}
                thickness={22}
                paddingAngle={2}
                strokeWidth={0}
                withTooltip
                tooltipDataSource="segment"
                chartLabel={total}
                style={{ flexShrink: 0 }}
              />
            )}

            <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
              {rows.map((r) => (
                <Box key={r.key}>
                  <Group justify="space-between" gap="xs" wrap="nowrap" mb={3}>
                    <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                      <Box
                        w={8}
                        h={8}
                        style={{ borderRadius: 2, background: r.colour, flexShrink: 0 }}
                      />
                      <Text fz="sm" fw={r.isBrand ? 600 : 400} truncate>
                        {r.name}
                      </Text>
                      {r.isBrand && (
                        <Badge size="xs" variant="light" color="blue">
                          You
                        </Badge>
                      )}
                    </Group>
                    <Text fz="sm" fw={r.isBrand ? 600 : 400} style={{ whiteSpace: "nowrap" }}>
                      {r.count}
                    </Text>
                  </Group>
                  <Box
                    h={6}
                    w="100%"
                    style={{
                      borderRadius: 3,
                      background: "var(--mantine-color-default-border)",
                    }}
                  >
                    <Box
                      h={6}
                      style={{
                        width: `${maxCount > 0 ? (r.count / maxCount) * 100 : 0}%`,
                        borderRadius: 3,
                        background: r.colour,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Group>

          <Text fz="xs" c="dimmed" mt="md">
            One answer usually names several brands, so these are shares of the names given, not
            of the {unaided.samples} answers behind them.
            {hiddenCount > 0 &&
              ` ${hiddenCount} further brand${
                hiddenCount === 1 ? " is" : "s are"
              } grouped into the gray slice.`}
          </Text>
        </>
      )}
    </Card>
  );
}

/**
 * The market the window was measured in.
 *
 * More than one is not an error: a brand that moved market mid-window has samples from both, and
 * they are not the same measurement. Saying "2 markets" and naming them in the tooltip is the
 * least this page can do about that — the alternative is a rate that silently spans two
 * populations, which every other number here refuses.
 */
function MarketLine({ markets }) {
  if (!markets?.length) return null;

  const [first, ...rest] = markets;
  if (!first.label) return null;

  if (rest.length === 0) {
    return (
      <Text fz="xs" c="dimmed">
        {first.label}
      </Text>
    );
  }

  return (
    <Tooltip
      withArrow
      multiline
      w={280}
      label={markets
        .map((m) => `${m.label ?? "unrecorded"} — ${m.samples} check${m.samples === 1 ? "" : "s"}`)
        .join("\n")}
    >
      <Group gap={4} wrap="nowrap" style={{ cursor: "help" }}>
        <Text fz="xs" c="warning">
          {markets.length} markets in this window
        </Text>
        <IconAlertTriangle size={12} style={{ opacity: 0.7 }} />
      </Group>
    </Tooltip>
  );
}

/** Groups the flat prompt list the API returns back into its classes, preserving order. */
function groupByClass(prompts) {
  const groups = [];
  for (const prompt of prompts) {
    const last = groups[groups.length - 1];
    if (last && last.class === prompt.class) last.rows.push(prompt);
    else groups.push({ class: prompt.class, rows: [prompt] });
  }
  return groups;
}

export default function BrandMentionDetection({ brand }) {
  const brandId = brand?.id;
  const [days, setDays] = useState("28");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openPrompt, setOpenPrompt] = useState(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Memoised because the drawer takes it as a dependency: a fresh object every render would
  // re-fetch the checks on each keystroke elsewhere on the page.
  const range = useMemo(() => {
    const to = new Date();
    return { from: new Date(to.getTime() - Number(days) * 86400000), to };
  }, [days]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchBrandMentions(brandId, range));
    } catch (e) {
      setError(e?.message ?? "Could not load detection data");
    } finally {
      setLoading(false);
    }
  }, [brandId, range]);

  useEffect(() => {
    if (brandId) load();
  }, [brandId, load]);

  if (loading) return <Skeleton h={320} radius="md" />;

  if (error) {
    return (
      <Alert color="error" icon={<IconAlertTriangle size={18} />} title="Detection unavailable">
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  const hasSamples = (data.totalSamples ?? 0) > 0 || (data.totalFailures ?? 0) > 0;
  const otherClasses = (data.classes ?? []).filter((c) => c.class !== "unaided");
  const unaidedClass = (data.classes ?? []).find((c) => c.class === "unaided");
  // Read from the class list, not from the headline's sample count: a window where every unaided
  // check happened to fail also has zero usable samples, and that is an outage, not a prompt list
  // that can never measure discovery.
  const noUnaidedPrompts =
    hasSamples && !(data.classes ?? []).some((c) => c.class === "unaided");

  const failures = data.totalFailures ?? 0;
  const failureShare = failures / Math.max((data.totalSamples ?? 0) + failures, 1);
  const failuresAreMaterial = failureShare >= FAILURE_ALERT_SHARE;

  const surfaces = data.surfaces ?? [];
  const soleSurface = surfaces.length === 1 ? surfaces[0] : null;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <div>
          <Title order={3}>Mention detection</Title>
          <Text fz="sm" c="dimmed">
            How often your brand comes up when we ask Google&apos;s AI surfaces a buyer&apos;s
            question that does not name you. Sampled daily.
          </Text>
        </div>
        <SegmentedControl value={days} onChange={setDays} data={WINDOWS} size="sm" />
      </Group>

      {/* Ahead of the "no samples yet" notice, which would otherwise read as "wait a day" to
          someone who switched sampling off and will be waiting forever. */}
      {data.detectionEnabled === false && (
        <Alert
          color="warning"
          icon={<IconAlertTriangle size={18} />}
          title="Daily sampling is switched off"
        >
          Nothing new is being measured for this brand. Anything below is history. Turn it back
          on under Daily detection in the monitor&apos;s settings.
        </Alert>
      )}

      {!hasSamples && data.detectionEnabled !== false && (
        <Alert color="info" icon={<IconInfoCircle size={18} />} title="No samples yet">
          Detection sampling runs once a day. The first results appear within 24 hours of
          enabling a Google surface on this brand&apos;s monitor.
        </Alert>
      )}

      {/* The most common way to read a zero here as a catastrophe: every prompt names the brand,
          so nothing on the list could ever have measured discovery in the first place. */}
      {noUnaidedPrompts && (
        <Alert
          color="warning"
          icon={<IconAlertTriangle size={18} />}
          title="Nothing here measures whether you are found"
        >
          Every prompt being sampled names your brand, so every answer repeats it back. Add at
          least one prompt phrased the way a buyer would ask — without your name in it — before
          this page can tell you anything about visibility.
        </Alert>
      )}

      {/* Only when the failures are eating enough of the window to explain a thin sample. A
          single failed fetch is a footnote on the summary card instead. */}
      {failuresAreMaterial && (
        <Alert
          color="warning"
          icon={<IconAlertTriangle size={18} />}
          title="Some checks could not be completed"
        >
          {failures} of {failures + (data.totalSamples ?? 0)} checks were excluded from this
          window because the fetch failed. Failed checks are never counted as “not mentioned”,
          so this window understates the number of observations rather than your visibility.
        </Alert>
      )}

      {hasSamples && (
        <Card withBorder padding="lg">
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xl">
            <DetectionHeadline
              rate={data.unaided}
              label={`Found without being named, last ${days} days`}
              citedNotNamed={unaidedClass?.citedNotNamed ?? 0}
            />

            {/* The surface, the market and the exceptions sit here rather than in a box of their
                own: with one surface that box was the headline again, at the same n. */}
            <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
              {soleSurface && (
                <Group gap={6} wrap="nowrap">
                  <LlmProviderLogo llmProvider={soleSurface.surface} size={16} />
                  <Text fz="sm" fw={500}>
                    {soleSurface.surfaceName}
                  </Text>
                </Group>
              )}
              {!soleSurface && surfaces.length > 1 && (
                <Text fz="sm" fw={500}>
                  {surfaces.length} surfaces
                </Text>
              )}

              {/* A rate is only ever about a market. Without this line a 0% reads as absolute
                  when it means "nobody in this country was told about you". */}
              <MarketLine markets={data.markets} />
              {failures > 0 && !failuresAreMaterial && (
                <Group gap={4} wrap="nowrap">
                  <Text fz="xs" c="dimmed">
                    {failures} check{failures === 1 ? "" : "s"} could not be fetched
                  </Text>
                  <Explainer
                    text={
                      "A failed fetch is dropped from the window rather than counted as “not " +
                      "mentioned”, so it lowers the number of observations and never the rate."
                    }
                  />
                </Group>
              )}
            </Stack>
          </Group>

          {otherClasses.length > 0 && (
            <>
              <Divider my="md" />
              <Stack gap="sm">
                {otherClasses.map((entry) => (
                  <ClassRow key={entry.class} entry={entry} />
                ))}
              </Stack>
            </>
          )}
        </Card>
      )}

      {/* Only when there is something to compare. See SurfaceTile. */}
      {surfaces.length > 1 && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {surfaces.map((s) => (
            <SurfaceTile key={s.surface} surface={s} />
          ))}
        </SimpleGrid>
      )}

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {data.unaided?.samples > 0 && (
          <ShareOfVoiceCard
            brandName={brand?.name}
            unaided={data.unaided}
            competitors={data.topCompetitors}
          />
        )}

        {data.topDomains?.length > 0 && (
          <Card withBorder padding="md">
            <Group justify="space-between" align="flex-start" mb={2} wrap="nowrap">
              <Group gap={6}>
                <Text fw={600}>Most cited sources</Text>
                <Explainer
                  text={
                    "From prompts that do not name you. An answer about your brand cites your " +
                    "own site, so counting those would describe your prompt list, not what the " +
                    "model reads when it is choosing who to name."
                  }
                />
              </Group>
              <Anchor
                component="button"
                type="button"
                fz="xs"
                onClick={() => setSourcesOpen(true)}
              >
                Explore all
              </Anchor>
            </Group>
            <Stack gap={6} mt="sm">
              {data.topDomains.slice(0, 10).map((d) => (
                <Group key={d.domain} justify="space-between" wrap="nowrap">
                  <Text fz="sm" truncate>
                    {d.domain}
                  </Text>
                  <Badge variant="light" color="gray">
                    {d.count}
                  </Badge>
                </Group>
              ))}
            </Stack>
          </Card>
        )}
      </SimpleGrid>

      {data.prompts?.length > 0 && (
        <Card withBorder padding="md">
          <Group gap={6} mb="xs">
            <Text fw={600}>By prompt</Text>
            <Explainer
              text={
                "Counts, not percentages: a week of daily checks is seven observations, far too " +
                "few for a per-prompt percentage to mean anything. Rows are grouped by what a " +
                "mention proves — 7 of 7 is a result in the first group and the expected " +
                "reading in the second. Open a row to see every check behind its count."
              }
            />
          </Group>
          <Table.ScrollContainer minWidth={620}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Prompt</Table.Th>
                  <Table.Th w={150}>Type</Table.Th>
                  <Table.Th w={120}>Mentioned</Table.Th>
                  <Table.Th w={120}>Last checked</Table.Th>
                  {/* The row is clickable, but a click target that only exists on the row is
                      unreachable by keyboard. This is the focusable version of the same action. */}
                  <Table.Th w={44} aria-label="Checks" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groupByClass(data.prompts).map((group) => {
                  const meta = presenceClassMeta(group.class);
                  return (
                    <Fragment key={group.class}>
                      <Table.Tr>
                        <Table.Td colSpan={5} bg="var(--mantine-color-default-hover)">
                          <Group gap="xs">
                            <Badge size="sm" color={meta.color} variant="light">
                              {meta.shortLabel}
                            </Badge>
                            <Text fz="xs" c="dimmed">
                              {meta.meaning}
                            </Text>
                          </Group>
                        </Table.Td>
                      </Table.Tr>

                      {group.rows.map((p) => (
                        <Table.Tr
                          key={p.monitoredQueryId}
                          onClick={() => setOpenPrompt(p)}
                          style={{ cursor: "pointer" }}
                        >
                          <Table.Td>
                            <Text fz="sm" lineClamp={2}>
                              {p.prompt ?? "(prompt removed)"}
                            </Text>
                            {/* The reference wording, for a reader who does not speak the market's
                                language. It is never what gets asked — see the localisation
                                service — so it is dimmed and second. */}
                            {p.promptInEnglish && (
                              <Text fz="xs" c="dimmed" lineClamp={2}>
                                {p.promptInEnglish}
                              </Text>
                            )}
                            {p.promptChangedAt && (
                              <Tooltip
                                label={
                                  "The wording changed inside this window. Checks before and " +
                                  "after the change asked different questions, so this count " +
                                  "spans two measurements rather than continuing one."
                                }
                                withArrow
                                multiline
                                w={300}
                              >
                                <Text fz="xs" c="warning" style={{ cursor: "help" }}>
                                  Reworded{" "}
                                  {new Date(p.promptChangedAt).toLocaleDateString()} — this count
                                  spans both
                                </Text>
                              </Tooltip>
                            )}
                            {p.failures > 0 && (
                              <Text fz="xs" c="dimmed">
                                {p.failures} failed check{p.failures === 1 ? "" : "s"} excluded
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <QueryTypeBadge queryType={p.queryType} />
                          </Table.Td>
                          <Table.Td>
                            <Text fz="sm" fw={600}>
                              {p.mentions}{" "}
                              <Text span c="dimmed" fw={400}>
                                / {p.samples}
                              </Text>
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text fz="xs" c="dimmed">
                              {p.lastSampledAt
                                ? new Date(p.lastSampledAt).toLocaleDateString()
                                : "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={`Show the checks behind ${
                                p.prompt ?? "this prompt"
                              }`}
                              onClick={() => setOpenPrompt(p)}
                            >
                              <IconChevronRight size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      {/* This page covers Google because of what a fetch costs, not because the product is about
          Google. Showing only Google without saying so reads as the second thing, and the gap
          between what the brand monitors and what this page measures belongs here. */}
      {data.notSampled?.length > 0 && (
        <Card withBorder padding="md">
          <Text fw={600} fz="sm" mb={4}>
            Monitored weekly, not sampled daily
          </Text>
          <Text fz="xs" c="dimmed" mb="sm">
            These still appear in your monitor reports. They are not in the numbers above.
          </Text>
          <Stack gap={8}>
            {data.notSampled.map((p) => (
              <Group key={p.name} gap="xs" wrap="nowrap" align="flex-start">
                <Badge size="sm" variant="light" color="gray" style={{ flexShrink: 0 }}>
                  {p.name}
                </Badge>
                <Text fz="xs" c="dimmed">
                  {p.reason}
                </Text>
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      {/* Settings, not a reading: collapsed until the aided row or a thin count sends someone
          looking for a missing spelling. It opens itself when there is a suggestion to accept. */}
      <BrandAliasesCard brand={brand} onSaved={load} collapsible />

      <SourceExplorerDrawer
        brandId={brandId}
        days={days}
        opened={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
      />

      <PromptSamplesDrawer
        brandId={brandId}
        prompt={openPrompt}
        range={range}
        onClose={() => setOpenPrompt(null)}
        ownDomain={hostOf(brand?.websiteUrl)}
      />
    </Stack>
  );
}
